import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type {
  GcLineItem,
  GcLineItemBillingMethod,
  GcLineItemInput,
  GcLineItemType,
} from "@/hooks/use-gc-line-items";

export const GC_TYPE_LABELS: Record<GcLineItemType, string> = {
  gc_fee: "GC Fee",
  general_conditions: "General Conditions",
  liability_insurance: "Liability Insurance",
  general_requirements: "General Requirements",
  owner_contingency: "Owner's Contingency",
  gc_contingency: "GC Contingency",
  custom: "Custom",
};

const CONTINGENCY_TYPES: GcLineItemType[] = ["owner_contingency", "gc_contingency"];

interface GcLineItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: GcLineItem | null;
  onSubmit: (input: GcLineItemInput) => void;
  isPending?: boolean;
}

export function GcLineItemDialog({
  open,
  onOpenChange,
  existing,
  onSubmit,
  isPending = false,
}: GcLineItemDialogProps) {
  const [lineType, setLineType] = useState<GcLineItemType>("general_conditions");
  const [label, setLabel] = useState("");
  const [budgetAmount, setBudgetAmount] = useState("");
  const [billingMethod, setBillingMethod] = useState<GcLineItemBillingMethod>("fixed_lump_sum");
  const [percentOfHardCosts, setPercentOfHardCosts] = useState("");
  const [ownerFacing, setOwnerFacing] = useState(true);
  const [retainageApplies, setRetainageApplies] = useState(true);
  const [retentionOverride, setRetentionOverride] = useState("");
  const [requiresReasonPerDraw, setRequiresReasonPerDraw] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setLineType(existing.lineType);
      setLabel(existing.label);
      setBudgetAmount(existing.budgetAmount);
      setBillingMethod(existing.billingMethod);
      setPercentOfHardCosts(existing.percentOfHardCosts ?? "");
      setOwnerFacing(existing.ownerFacing);
      setRetainageApplies(existing.retainageApplies);
      setRetentionOverride(existing.retentionPercentOverride ?? "");
      setRequiresReasonPerDraw(existing.requiresReasonPerDraw);
      setNotes(existing.notes ?? "");
    } else {
      setLineType("general_conditions");
      setLabel("");
      setBudgetAmount("");
      setBillingMethod("fixed_lump_sum");
      setPercentOfHardCosts("");
      setOwnerFacing(true);
      setRetainageApplies(true);
      setRetentionOverride("");
      setRequiresReasonPerDraw(false);
      setNotes("");
    }
  }, [open, existing]);

  // Auto-label + auto-defaults when the user picks a preset type on a new row.
  useEffect(() => {
    if (existing) return;
    if (!open) return;
    if (lineType === "custom") return;
    setLabel(GC_TYPE_LABELS[lineType]);
    if (CONTINGENCY_TYPES.includes(lineType)) {
      setRequiresReasonPerDraw(true);
      setRetainageApplies(false);
    }
    if (lineType === "gc_fee") {
      setBillingMethod("percent_of_hard_costs");
    }
  }, [lineType, open, existing]);

  const isPercent = billingMethod === "percent_of_hard_costs";

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    if (Number.isNaN(Number(budgetAmount)) || Number(budgetAmount) < 0) return;
    const payload: GcLineItemInput = {
      lineType,
      label: label.trim(),
      budgetAmount: budgetAmount === "" ? "0" : budgetAmount,
      billingMethod,
      percentOfHardCosts: isPercent && percentOfHardCosts ? percentOfHardCosts : null,
      ownerFacing,
      retainageApplies,
      retentionPercentOverride: retentionOverride || null,
      requiresReasonPerDraw,
      notes: notes || null,
    };
    onSubmit(payload);
  }

  const canSubmit =
    label.trim().length > 0 &&
    !Number.isNaN(Number(budgetAmount)) &&
    Number(budgetAmount) >= 0 &&
    !isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit GC line item" : "Add GC line item"}</DialogTitle>
          <DialogDescription>
            Baseline entry for a GC-owned cost line. These budgets seed the owner pay app each
            period and feed the G703 AIA form.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="gc-type">Type</Label>
              <Select value={lineType} onValueChange={(v) => setLineType(v as GcLineItemType)}>
                <SelectTrigger id="gc-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(GC_TYPE_LABELS) as GcLineItemType[]).map((k) => (
                    <SelectItem key={k} value={k}>
                      {GC_TYPE_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gc-billing">Billing method</Label>
              <Select
                value={billingMethod}
                onValueChange={(v) => setBillingMethod(v as GcLineItemBillingMethod)}
              >
                <SelectTrigger id="gc-billing">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed_lump_sum">Fixed lump sum</SelectItem>
                  <SelectItem value="percent_of_hard_costs">Percentage of hard costs</SelectItem>
                  <SelectItem value="manual_per_period">Manual per period</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gc-label">Label</Label>
            <Input
              id="gc-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. General Conditions"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="gc-budget">Budget amount ($)</Label>
              <Input
                id="gc-budget"
                type="number"
                step="0.01"
                min="0"
                value={budgetAmount}
                onChange={(e) => setBudgetAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            {isPercent ? (
              <div className="space-y-1.5">
                <Label htmlFor="gc-pct">Percent of hard costs (0.0000 – 1.0000)</Label>
                <Input
                  id="gc-pct"
                  type="number"
                  step="0.0001"
                  min="0"
                  max="1"
                  value={percentOfHardCosts}
                  onChange={(e) => setPercentOfHardCosts(e.target.value)}
                  placeholder="0.0500"
                />
                <p className="text-xs text-gray-500">Enter as a decimal — e.g. 0.05 for 5%.</p>
              </div>
            ) : (
              <div />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="gc-retention">Retention % override</Label>
              <Input
                id="gc-retention"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={retentionOverride}
                onChange={(e) => setRetentionOverride(e.target.value)}
                placeholder="Uses prime contract default if blank"
                disabled={!retainageApplies}
              />
            </div>
            <div className="space-y-2 pt-6">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={retainageApplies}
                  onCheckedChange={(v) => setRetainageApplies(v === true)}
                />
                Retainage applies
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={ownerFacing}
                  onCheckedChange={(v) => setOwnerFacing(v === true)}
                />
                Show to owner on G702 / G703
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={requiresReasonPerDraw}
                  onCheckedChange={(v) => setRequiresReasonPerDraw(v === true)}
                />
                Require reason per draw
              </label>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gc-notes">Notes (internal)</Label>
            <Textarea
              id="gc-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {existing ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
